import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { AuthContext, type AuthContextValue } from "./AuthContext";
import { setAuthHandle } from "./authBridge";

const domain = import.meta.env.VITE_AUTH0_DOMAIN ?? "";
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID ?? "";
const audience = import.meta.env.VITE_AUTH0_AUDIENCE ?? "";

function Auth0Bridge({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    user,
    error,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  useEffect(() => {
    setAuthHandle({
      getAuthorization: async () => {
        if (!isAuthenticated) return null;
        try {
          const token = await getAccessTokenSilently();
          return token ? `Bearer ${token}` : null;
        } catch {
          return null;
        }
      },
      refresh: async () => {
        try {
          // `cacheMode: 'off'` skips the in-memory copy and re-runs the
          // refresh-token grant, which is the whole point of retrying a 401.
          await getAccessTokenSilently({ cacheMode: "off" });
        } catch {
          /* the retry will 401 again and fall through to onUnauthorized */
        }
      },
      onUnauthorized: () => {
        void loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}`,
          },
        });
      },
    });
    return () => setAuthHandle(null);
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  const signIn = useCallback<AuthContextValue["signIn"]>(
    (options) =>
      loginWithRedirect({
        appState: { returnTo: options?.returnTo ?? "/" },
        authorizationParams: options?.email
          ? { login_hint: options.email }
          : undefined,
      }),
    [loginWithRedirect],
  );

  const signOut = useCallback(
    () => logout({ logoutParams: { returnTo: window.location.origin } }),
    [logout],
  );

  // The screen deliberately says something bland and human — an applicant is
  // not helped by "Service not found: https://api.livingwill.io". But that
  // string is the entire diagnosis when a tenant is misconfigured, and
  // discarding it makes first-time setup a guessing game. So it goes to the
  // console, where the person wiring up Auth0 will look and an applicant never
  // will.
  useEffect(() => {
    if (!error) return;
    const detail = error as Error & {
      error?: string;
      error_description?: string;
    };
    console.error(
      "[auth0] sign-in failed:",
      detail.error ?? detail.name,
      "—",
      detail.error_description ?? detail.message,
      detail,
    );
  }, [error]);

  const value = useMemo<AuthContextValue>(
    () => ({
      mode: "auth0",
      isLoading,
      isAuthenticated,
      email: (user?.email as string | undefined) ?? null,
      error: error ? "We couldn't sign you in. Try again in a moment." : null,
      // Null on purpose. In Auth0 mode the roles live in the access token and
      // are resolved server-side; the guards ask the API which area this
      // account belongs to rather than trusting anything decided here.
      role: null,
      signIn,
      signOut,
    }),
    [isLoading, isAuthenticated, user, error, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Authorization Code + PKCE.
 *
 * `cacheLocation: 'memory'` and `useRefreshTokens` are not defaults we can
 * take or leave: a token in localStorage is readable by any script that ever
 * gets injected into the origin, and this application holds identity documents.
 * Tokens live in a closure for the lifetime of the tab and are re-minted from a
 * rotating refresh token after that.
 */
export function Auth0AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      cacheLocation="memory"
      useRefreshTokens
      useRefreshTokensFallback={false}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: audience || undefined,
        scope: "openid profile email offline_access",
      }}
      onRedirectCallback={(appState) => {
        navigate(appState?.returnTo ?? "/", { replace: true });
      }}
    >
      <Auth0Bridge>{children}</Auth0Bridge>
    </Auth0Provider>
  );
}
